/**
 * 1-bit monokrom BMP encoder — etiket yazıcıları için.
 *
 * CANLI TESTLE DOĞRULANMIŞ DAVRANIŞ (Argox OS-214 plus, PPLA, 203 dpi):
 *
 *  1) Yazıcı BMP paletini YOK SAYAR. Standart BMP'de palet indeksi anlamı
 *     belirler, ama yazıcı doğrudan bite bakıyor: **bit 0 = mürekkep (siyah)**,
 *     bit 1 = boş. Yani veri, standart BMP'nin tersi polaritede yazılmalı.
 *     Palet yine de doğru yazılıyor ki dosya standart görüntüleyicilerde de
 *     açılabilsin (hata ayıklarken BMP'yi diske kaydedip bakmak işe yarıyor).
 *
 *  2) Yazıcı ilk veri satırını etiketin ÜST satırına basar (top-down).
 *     Standart BMP ise satırları alttan üste saklar. Bu yüzden satır sırası
 *     ters çevrilmez — görüntünün üst satırı ilk yazılır. Header'da yükseklik
 *     pozitif kalıyor (yani dosya "bottom-up" iddia ediyor); yazıcı için doğru
 *     olan bu, ama BMP'yi bir görüntüleyicide açarsanız dikey ters görünür.
 *
 * Bu iki davranış `inkBitZero` ve `topDown` seçenekleriyle kapatılabilir —
 * başka marka bir yazıcı ters davranırsa teşhis için gerekli.
 */

/** Mürekkep maskesi: ink[y * width + x] sıfırdan farklıysa o nokta siyah */
export interface MonoBitmap {
  width: number;
  height: number;
  ink: Uint8Array;
}

export interface Bmp1BitOptions {
  /** true: mürekkep biti 0 (Argox davranışı, varsayılan) */
  inkBitZero?: boolean;
  /** true: ilk veri satırı görüntünün üst satırı (varsayılan) */
  topDown?: boolean;
}

export interface Bmp1BitResult {
  /** Tam BMP dosyası (header + palet + piksel verisi) */
  bytes: Uint8Array;
  /** 32 dot katına hizalanmış genişlik */
  widthDots: number;
  heightDots: number;
  /** Satır başına byte sayısı */
  stride: number;
}

const HEADER_SIZE = 62; // 14 (file) + 40 (info) + 8 (2 renk palet)

/** Yazıcı satır verisi 32 dot katına hizalı olmak zorunda */
export function alignTo32(dots: number): number {
  return Math.ceil(dots / 32) * 32;
}

export function encodeBmp1Bit(mono: MonoBitmap, options?: Bmp1BitOptions): Bmp1BitResult {
  const inkBitZero = options?.inkBitZero ?? true;
  const topDown = options?.topDown ?? true;

  const { width: srcWidth, height, ink } = mono;
  if (srcWidth <= 0 || height <= 0) {
    throw new Error('Geçersiz bitmap ölçüsü');
  }

  const widthDots = alignTo32(srcWidth);
  const stride = Math.ceil(widthDots / 32) * 4;
  const pixelDataSize = stride * height;

  const bytes = new Uint8Array(HEADER_SIZE + pixelDataSize);
  const view = new DataView(bytes.buffer);

  // --- Dosya başlığı (14 byte) ---
  bytes[0] = 0x42; // 'B'
  bytes[1] = 0x4d; // 'M'
  view.setUint32(2, bytes.length, true);
  view.setUint32(6, 0, true);
  view.setUint32(10, HEADER_SIZE, true);

  // --- Bilgi başlığı (40 byte) ---
  view.setUint32(14, 40, true);
  view.setInt32(18, widthDots, true);
  view.setInt32(22, height, true);
  view.setUint16(26, 1, true); // planes
  view.setUint16(28, 1, true); // bit count
  view.setUint32(30, 0, true); // sıkıştırma yok
  view.setUint32(34, pixelDataSize, true);
  view.setUint32(38, 0, true); // x çözünürlük (kullanılmıyor)
  view.setUint32(42, 0, true); // y çözünürlük
  view.setUint32(46, 2, true); // palet renk sayısı
  view.setUint32(50, 0, true);

  // --- Palet: indeks 0 beyaz, indeks 1 siyah (BGRA) ---
  bytes[54] = 0xff;
  bytes[55] = 0xff;
  bytes[56] = 0xff;
  bytes[57] = 0x00;
  bytes[58] = 0x00;
  bytes[59] = 0x00;
  bytes[60] = 0x00;
  bytes[61] = 0x00;

  // --- Piksel verisi ---
  // Mürekkep biti 0 ise boş alan 1'lerle dolu başlar, sonra mürekkep bitleri silinir
  if (inkBitZero) {
    bytes.fill(0xff, HEADER_SIZE);
  }

  for (let y = 0; y < height; y++) {
    // topDown: görüntünün y'inci satırı dosyanın y'inci satırına yazılır
    const row = topDown ? y : height - 1 - y;
    const rowBase = HEADER_SIZE + row * stride;
    const srcBase = y * srcWidth;

    for (let x = 0; x < srcWidth; x++) {
      if (ink[srcBase + x] === 0) continue;

      const byteIndex = rowBase + (x >> 3);
      const mask = 1 << (7 - (x & 7));

      if (inkBitZero) {
        bytes[byteIndex] &= ~mask & 0xff;
      } else {
        bytes[byteIndex] |= mask;
      }
    }
  }

  return { bytes, widthDots, heightDots: height, stride };
}
